// NOAA GOES Enhanced Satellite (visible) layer family.
//
// Iowa State Mesonet exposes the GOES-East 1km visible channel as a
// TMS (note: 'goes_east_geocolor' is NOT a Mesonet layer name;
// 'goes-east-vis-1km-900913' is the correct CHANNEL 02 product).
// Visible imagery is only useful in daylight — the IR layer (satir) is
// the always-available companion.
//
// Extracted from addOverlays.ts as HI-1 batch 6b (audit 2026-05-22).
// Pure mechanical move — source URL template, paint opacity, and
// before-id stack order are byte-identical to the pre-extraction form.

import type { Map as MlMap } from 'maplibre-gl';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the GOES visible-color satellite source + raster layer.
 * Idempotent on the source.
 */
export function addSatGeoColorLayers(map: MlMap): void {
  if (!map.getSource('satgeocolor')) {
    map.addSource('satgeocolor', {
      type: 'raster',
      // HI-7 audit 2026-05-22: proxy through /api/tile so we pick up
      // the SW cache + 8s upstream timeout. Direct fetches to
      // iastate.edu had no timeout and would stall MapLibre when the
      // host went slow on a flaky LTE link.
      tiles: [
        '/api/tile?layer=goes-east-vis&z={z}&x={x}&y={y}',
      ],
      tileSize: 256,
      attribution: 'Imagery: NOAA GOES (vis) · Iowa State Mesonet',
      minzoom: 0,
      maxzoom: 9,
    });
    const beforeId = (() => {
      for (const id of ['sua-fill', 'tfr-fill', 'fires-fill', 'sfcanalysis-raster', 'nexrad-raster']) {
        if (map.getLayer(id)) return id;
      }
      return undefined;
    })();
    map.addLayer(
      {
        id: 'satgeocolor-raster',
        type: 'raster',
        source: 'satgeocolor',
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 0.75 },
      },
      beforeId,
    );
  }
}

registerLayerFamily({
  id: 'satgeocolor',
  visibilitySelector: (l) => l.satgeocolor,
  label: 'GOES visible satellite',
  sourceId: 'satgeocolor',
  layerIds: ['satgeocolor-raster'],
  mount: (map) => addSatGeoColorLayers(map),
  // Restore current visibility after a basemap rebuild — see nexrad.ts.
  applyState: (map) => {
    if (!map.getLayer('satgeocolor-raster')) return;
    const show = useLayerVisibilityStore.getState().layers.satgeocolor;
    map.setLayoutProperty('satgeocolor-raster', 'visibility', show ? 'visible' : 'none');
  },
});
